import type { ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import type { InstanceApi } from '@eis/insuranceservice-sdk';
import {
  NOT_STATED,
  currencyOfPremium,
  formatAmount,
  formatDelta,
  formatRate,
  isKnownEngineError,
  isKnownReason,
  periodOf,
  taxAmountOf,
  taxCodesOf,
  taxModeOfPremium,
  totalsOfPremium,
} from '../model/format';

/**
 * Where the estimate stands. `absent` is a premium that has no estimate to show beside it (a
 * story's isolated breakdown); the others are the query's states — and `ready` may still carry
 * `estimate.error`: a failing engine is ANSWERED, never a failed request (SDD-021 §7.4).
 */
export type EstimateState =
  | { readonly status: 'absent' }
  | { readonly status: 'loading' }
  | { readonly status: 'failed' }
  | { readonly status: 'ready'; readonly estimate: InstanceApi.PremiumEstimate };

/**
 * *Tatsächlich* and *Geschätzt* side by side (SDD-021 §9, PC-21): net, tax per code and gross per
 * period — and the monthly equivalent when the period is not a month — with the difference and
 * the reasons as chips.
 *
 * *Tatsächlich* is the premium the page already has (the snapshot in force, the lead's priced
 * draft), filled from the estimate's `actual` only where the premium itself states nothing.
 * *Geschätzt* arrives after the page; its error is said INLINE, and the actual figures stay.
 * Every figure is the service's decimal string through `formatCurrency` — the difference too:
 * it is the estimate's `delta`, never a subtraction made here (prompt 29 Decision 9).
 */
export function PremiumEstimate({
  premium,
  estimate,
  locale,
}: {
  readonly premium: InstanceApi.Premium | undefined;
  readonly estimate: EstimateState;
  readonly locale: string;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const ready = estimate.status === 'ready' ? estimate.estimate : undefined;
  const error = ready?.error;
  const estimated = error === undefined ? ready?.estimated : undefined;
  const own = totalsOfPremium(premium);
  const actual: InstanceApi.PremiumTotals = {
    net: own.net ?? ready?.actual?.net,
    tax: own.tax ?? ready?.actual?.tax,
    gross: own.gross ?? ready?.actual?.gross,
  };
  const currency = premium === undefined ? (ready?.currency ?? 'EUR') : currencyOfPremium(premium);
  const period = periodOf(premium?.period ?? ready?.period);
  const included = taxModeOfPremium(premium) === 'INCLUDED';
  const showEstimate = estimate.status !== 'absent';
  // The monthly equivalent, when the period is not already a month (absent for ONE_TIME).
  const monthlyActual = period === 'PER_MONTH' ? undefined : premium?.monthlyEquivalent;
  const monthlyEstimated =
    period === 'PER_MONTH' ? undefined : ready?.estimatedPremium?.monthlyEquivalent;

  const estimatedCell = (value: string | undefined): ReactNode => {
    if (estimate.status === 'loading') {
      return <Skeleton variant="text" width={64} sx={{ ml: 'auto' }} />;
    }
    return value === undefined ? NOT_STATED : formatAmount(value, currency, locale);
  };

  const block = (
    label: string,
    actualTotals: InstanceApi.PremiumTotals | undefined,
    estimatedTotals: InstanceApi.PremiumTotals | undefined,
    testId: string,
  ): ReactNode => {
    const codes = taxCodesOf(actualTotals?.tax, estimatedTotals?.tax);
    const rows: {
      key: string;
      label: ReactNode;
      actual?: string;
      estimated?: string;
      strong?: boolean;
    }[] = [
      {
        key: 'net',
        label: t('premium.net'),
        actual: actualTotals?.net,
        estimated: estimatedTotals?.net,
      },
      ...codes.map((code) => ({
        key: `tax-${code.code}`,
        label: (
          <>
            {t('premium.taxLine', { code: code.code, rate: formatRate(code.rate, locale) })}
            {included && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {t('premium.included')}
              </Typography>
            )}
          </>
        ),
        actual: taxAmountOf(actualTotals?.tax, code.code),
        estimated: taxAmountOf(estimatedTotals?.tax, code.code),
      })),
      {
        key: 'gross',
        label: t('premium.gross'),
        actual: actualTotals?.gross,
        estimated: estimatedTotals?.gross,
        strong: true,
      },
    ];
    return (
      <>
        <TableRow>
          <TableCell colSpan={showEstimate ? 3 : 2} sx={{ bgcolor: 'action.hover', py: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {label}
            </Typography>
          </TableCell>
        </TableRow>
        {rows.map((row) => (
          <TableRow key={row.key} data-testid={`${testId}-${row.key}`}>
            <TableCell sx={{ fontWeight: row.strong === true ? 600 : 400 }}>{row.label}</TableCell>
            <TableCell
              align="right"
              sx={{ fontWeight: row.strong === true ? 600 : 400 }}
              data-testid="actual"
            >
              {formatAmount(row.actual, currency, locale)}
            </TableCell>
            {showEstimate && (
              <TableCell
                align="right"
                sx={{ fontWeight: row.strong === true ? 600 : 400 }}
                data-testid="estimated"
              >
                {estimatedCell(row.estimated)}
              </TableCell>
            )}
          </TableRow>
        ))}
      </>
    );
  };

  const reasons = error === undefined ? (ready?.reasons ?? []) : [];

  return (
    <Stack spacing={1.5}>
      <Table size="small" aria-label={t('premium.compare.label')} data-testid="premium-compare">
        <TableHead>
          <TableRow>
            <TableCell>{t('premium.compare.line')}</TableCell>
            <TableCell align="right">{t('premium.compare.actual')}</TableCell>
            {showEstimate && <TableCell align="right">{t('premium.compare.estimated')}</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {block(t(`premium.period.${period}`), actual, estimated, 'period')}
          {(monthlyActual !== undefined || monthlyEstimated !== undefined) &&
            block(t('premium.compare.monthly'), monthlyActual, monthlyEstimated, 'monthly')}
          {ready?.delta?.gross !== undefined && error === undefined && (
            <TableRow data-testid="premium-delta">
              <TableCell>{t('premium.compare.delta')}</TableCell>
              <TableCell />
              <TableCell align="right">
                {formatDelta(ready.delta.gross, currency, locale)}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {estimate.status === 'loading' && (
        <Typography variant="body2" color="text.secondary" role="status">
          {t('premium.estimate.loading')}
        </Typography>
      )}

      {reasons.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{ alignItems: 'center', flexWrap: 'wrap' }}
          data-testid="estimate-reasons"
        >
          <Typography variant="caption" color="text.secondary">
            {t('premium.compare.reasons')}
          </Typography>
          {reasons.map((reason) => (
            <Chip
              key={reason}
              size="small"
              variant="outlined"
              color={reason === 'NONE' ? 'default' : 'warning'}
              label={isKnownReason(reason) ? t(`premium.reason.${reason}`) : reason}
            />
          ))}
        </Stack>
      )}

      {(estimate.status === 'failed' || error !== undefined) && (
        <Alert severity="warning" data-testid="estimate-error">
          {estimate.status === 'failed'
            ? t('premium.estimate.requestFailed')
            : `${t('premium.estimate.error')} ${
                isKnownEngineError(error?.code ?? '')
                  ? t(`premium.estimate.errorCode.${error?.code ?? ''}` as never)
                  : t('premium.estimate.errorCode.other')
              }`}
          {(error?.items ?? []).length > 0 && (
            <Typography variant="body2" component="span" sx={{ display: 'block', mt: 0.5 }}>
              {(error?.items ?? []).join(' · ')}
            </Typography>
          )}
        </Alert>
      )}
    </Stack>
  );
}
